import {useState} from 'react';

import {defaultTo, equals, not} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {IdentifiableBasketItem} from '../types/basket/IdentifiableBasketItem';

/**
 * Tracks a set of basket item ids that is kept in sync with the basket contents.
 *
 * Ids of items that are no longer in the basket are pruned during render, which is what
 * keeps a flagged item from blocking basket actions forever after it has been deleted -
 * e.g. an item whose quantity edit never reached the server because the user removed it
 * while the change was still waiting in the debounce.
 */
export function useBasketItemIdsSet(basketItems: IdentifiableBasketItem[] | Nullish) {
  const [ids, setIds] = useState<string[]>([]);

  const basketItemsIds = defaultTo([], basketItems).map((basketItem) => basketItem.id);

  const presentIds = ids.filter((id) => basketItemsIds.includes(id));

  if (not(equals(ids, presentIds))) {
    setIds(presentIds);
  }

  const setId = (itemId: string, isIncluded: boolean) =>
    setIds((prevIds) => {
      const idsWithoutItem = prevIds.filter((id) => not(equals(id, itemId)));

      // An item that is not in the basket must never enter the set - there would be
      // nothing left to clear the flag once it is gone.
      const canBeIncluded = isIncluded && basketItemsIds.includes(itemId);

      return canBeIncluded ? [...idsWithoutItem, itemId] : idsWithoutItem;
    });

  return {ids: presentIds, hasIds: isNotNilOrEmpty(presentIds), setId};
}
